<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Appraisal</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Create Special Approval</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <form @submit.prevent>
          <div class="crud-form position-relative">
            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">{{ step === 1 ? 'Special Approval' : 'Sub-special Approval' }}</h1>
              </div>

              <!-- Stepper -->
              <div class="header-box d-flex justify-content-between align-items-center mb-3">
                <div class="progress-bar" :style="{ '--progress-width': progressWidth, '--step-count': totalSteps }">
                  <div class="step-icon" v-for="(s, i) in totalSteps" :key="'icon-' + i">
                    <div :class="['step', { active: step === i + 1, completed: step > i + 1 }]">
                      {{ step > i + 1 ? '✓' : i + 1 }}
                    </div>
                  </div>
                  <div class="progress-line" :style="{ '--progress-width': progressWidth }" />
                  <div class="step-label text-wrap" v-for="(s, i) in steps.slice(0, totalSteps)" :key="'label-' + i"
                    :class="{ active: step === i + 1 }">
                    {{ s.label }}
                  </div>
                </div>
              </div>

              <!-- ─── STEP 1: Special Approval ─── -->
              <div v-if="step === 1">
                <div class="form-body">

                  <!-- 2-column field grid -->
                  <div class="row g-3 mb-4">

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Budget Year</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <select class="form-select" v-model="form.budgetYear">
                            <option value="" disabled>- Select Year -</option>
                            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Nature of Expenditure</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <select class="form-select" v-model="form.natureOfExpenditure">
                            <option value="" disabled>- Select Type -</option>
                            <option value="3">Capital</option>
                            <option value="2">Recurrent</option>
                            <option value="4">Hybrid</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Approval Date</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <input type="date" class="form-control" v-model="form.approvalDate" />
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">File Number</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <input type="text" class="form-control" v-model="form.fileNumber" />
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Creator Name</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <input type="text" class="form-control" v-model="form.creatorName" disabled />
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Sectoral Head Name</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <input type="text" class="form-control" v-model="form.sectoralHeadName" disabled />
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Purpose of Request</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <input type="text" class="form-control" v-model="form.purposeOfRequest" />
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Amount Required</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <input type="text" class="form-control"
                            :value="formatAmount(form.amountRequired)"
                            @input="(e) => onAmountInput(e, form, 'amountRequired')"
                            placeholder="0.00" />
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="row form-group floating-label-group m-0">
                        <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                          <label class="form-label">Amount Approved</label>
                        </div>
                        <div class="col-md-8 input-right">
                          <input type="text" class="form-control"
                            :value="formatAmount(form.amountApproved)"
                            @input="(e) => onAmountInput(e, form, 'amountApproved')"
                            placeholder="0.00" />
                        </div>
                      </div>
                    </div>

                  </div>

                  <!-- Loading state -->
                  <div v-if="ecoLoading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                    <p class="mt-2 mb-0 text-muted">Loading eco line items...</p>
                  </div>

                  <!-- Eco section (only after year + nature are selected and data is loaded) -->
                  <template v-if="form.budgetYear && form.natureOfExpenditure && !ecoLoading">

                  <!-- Eco Line Items (hidden when multilayered — Step 2 handles them per sub-approval) -->
                  <template v-if="!multilayered">

                  <h5 class="section-title">Eco Line Items</h5>

                  <div class="mt-3">
                    <h6 class="text-center mb-3">Select Eco Line Item to Add:</h6>
                    <div class="d-flex justify-content-center">
                      <div class="d-flex align-items-end gap-2 flex-wrap">
                        <div class="d-flex flex-column" style="max-width: 160px;">
                          <label class="form-label mb-1 text-muted" style="font-size: 13px;">Filter Options</label>
                          <input type="text" v-model="searchTerm" placeholder="Search title/code"
                            class="form-control form-control-sm" />
                        </div>
                        <div class="d-flex flex-column" style="max-width: 220px;">
                          <label class="form-label mb-1 text-muted" style="font-size: 13px;">Choose Eco Line Item</label>
                          <select class="form-select form-select-sm" v-model="newEcoId" @change="onSelectEco"
                            :disabled="ecoLoading || !form.budgetYear">
                            <option value="" disabled>-- pick code --</option>
                            <option v-for="opt in filteredEcoOptions" :key="opt.id" :value="opt.id">
                              {{ opt.ecoCode }} — {{ opt.title }}
                            </option>
                          </select>
                        </div>
                        <div class="d-flex flex-column">
                          <label class="invisible mb-1">Add</label>
                          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="!newEcoId"
                            @click="addEcoItem">
                            <span class="iconify" data-icon="mdi:plus"></span>
                            Add Item
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="table-responsive mt-4">
                    <table class="table table-bordered text-center align-middle">
                      <thead class="table-light">
                        <tr>
                          <th>Eco Code</th>
                          <th>Title</th>
                          <th>Approved Budget (#)</th>
                          <th>Approval Release To Date (#)</th>
                          <th>Balance</th>
                          <th>Performance</th>
                          <th>Amount Required</th>
                          <th>Amount Approved</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="ecoItems.length === 0">
                          <td colspan="9" class="text-muted fst-italic py-3">No economic line item selected yet.</td>
                        </tr>
                        <tr v-for="(item, index) in ecoItems" :key="item.id"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td>{{ item.ecoCode }}</td>
                          <td>{{ item.title }}</td>
                          <td>{{ Number(item.approvedBudget || 0).toLocaleString() }}</td>
                          <td>{{ Number(item.releasedToDate || 0).toLocaleString() }}</td>
                          <td>{{ Number(item.balance || 0).toLocaleString() }}</td>
                          <td>{{ item.performance }}%</td>
                          <td>
                            <input type="text" class="form-control text-center"
                              :value="formatAmount(item.amountRequired)"
                              @input="(e) => onEcoAmountInput(e, item)"
                              placeholder="0.00" />
                          </td>
                          <td>
                            <input type="text" class="form-control text-center"
                              :value="formatAmount(item.amountApproved)"
                              @input="(e) => onEcoApprovedInput(e, item)"
                              placeholder="0.00" />
                          </td>
                          <td>
                            <button type="button" class="btn btn-outline-danger btn-sm" @click="removeEcoItem(index)">
                              <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  </template>

                  <!-- Approval Document -->
                  <h5 class="section-title mt-4">Approval Document</h5>
                  <div
                    class="upload-area"
                    :class="{ dragging: isDragging }"
                    @click="openFilePicker"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="onDrop">
                    <input type="file" ref="fileInput" class="d-none"
                      accept=".pdf,image/jpeg,image/png"
                      @change="onFileChange" />
                    <span class="material-symbols-outlined upload-icon">cloud_upload</span>
                    <p class="mb-1">Click to upload or drag and drop</p>
                    <p class="text-muted small mb-0">PDF, JPG or PNG (max. 1MB each)</p>
                    <p v-if="documentName" class="text-success small mt-2 mb-0">{{ documentName }}</p>
                  </div>

                  <!-- Multilayered checkbox -->
                  <div class="mt-3 d-flex align-items-center gap-2">
                    <input class="form-check-input mt-0" type="checkbox" id="multilayered" v-model="multilayered" />
                    <label class="form-check-label" for="multilayered">Multilayered sub-special approval</label>
                  </div>

                  </template>

                </div>
              </div>

              <!-- ─── STEP 2: Sub-special Approval ─── -->
              <div v-if="step === 2">
                <div class="form-body">

                  <div v-for="(sub, index) in subApprovals" :key="index" class="sub-approval-card mb-4">

                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h5 class="section-title mb-0">{{ subApprovalLabel(index) }}</h5>
                      <button v-if="subApprovals.length > 1" type="button"
                        class="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                        @click="removeSubApproval(index)">
                        <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                        Delete
                      </button>
                    </div>

                    <div class="row g-3 mb-3">
                      <div class="col-md-12">
                        <div class="row form-group position-relative floating-label-group m-0">
                          <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                            <label class="form-label">Special Approval Type:</label>
                          </div>
                          <div class="col-md-9 input-right">
                            <select class="form-select" v-model="sub.typeId">
                              <option value="" disabled>- Select Type -</option>
                              <option v-for="t in specialApprovalTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="row form-group floating-label-group m-0">
                          <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                            <label class="form-label">Amount Required</label>
                          </div>
                          <div class="col-md-8 input-right">
                            <input type="text" class="form-control"
                              :value="formatAmount(sub.amtRequired)"
                              @input="(e) => onAmountInput(e, sub, 'amtRequired')"
                              placeholder="0.00" />
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="row form-group floating-label-group m-0">
                          <div class="col-md-4 label-left d-flex justify-content-end align-items-center">
                            <label class="form-label">Amount Approved</label>
                          </div>
                          <div class="col-md-8 input-right">
                            <input type="text" class="form-control"
                              :value="formatAmount(sub.amtApproved)"
                              @input="(e) => onAmountInput(e, sub, 'amtApproved')"
                              placeholder="0.00" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <h5 class="section-title">Eco Line Item</h5>

                    <div class="mt-3">
                      <h6 class="text-center mb-3">Select Eco-line Item to Add:</h6>
                      <div class="d-flex justify-content-center">
                        <div class="d-flex align-items-end gap-2 flex-wrap">
                          <div class="d-flex flex-column" style="max-width: 160px;">
                            <label class="form-label mb-1 text-muted" style="font-size: 13px;">Filter Options</label>
                            <input type="text" v-model="sub.searchTerm" placeholder="Search title/code"
                              class="form-control form-control-sm" />
                          </div>
                          <div class="d-flex flex-column" style="max-width: 220px;">
                            <label class="form-label mb-1 text-muted" style="font-size: 13px;">Choose Eco Line Item</label>
                            <select class="form-select form-select-sm" v-model="sub.newEcoId">
                              <option value="" disabled>-- pick code --</option>
                              <option v-for="opt in getFilteredSubEcoOptions(sub)" :key="opt.id" :value="opt.id">
                                {{ opt.ecoCode }} — {{ opt.title }}
                              </option>
                            </select>
                          </div>
                          <div class="d-flex flex-column">
                            <label class="invisible mb-1">Add</label>
                            <button type="button" class="btn btn-outline-primary btn-sm"
                              :disabled="!sub.newEcoId" @click="addSubEcoItem(sub)">
                              + Add Item
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="table-responsive mt-4">
                      <table class="table table-bordered text-center align-middle">
                        <thead class="table-light">
                          <tr>
                            <th>Eco Code</th>
                            <th>Title</th>
                            <th>Approved Budget (#)</th>
                            <th>Approval Release To Date (#)</th>
                            <th>Balance</th>
                            <th>Performance</th>
                            <th>Amount Required</th>
                            <th>Amount Approved</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="sub.ecoItems.length === 0">
                            <td colspan="9" class="text-muted fst-italic py-3">No eco line item selected yet.</td>
                          </tr>
                          <tr v-for="(item, i) in sub.ecoItems" :key="item.id"
                            :class="i % 2 === 0 ? 'row-even' : 'row-odd'">
                            <td>{{ item.ecoCode }}</td>
                            <td>{{ item.title }}</td>
                            <td>{{ Number(item.approvedBudget || 0).toLocaleString() }}</td>
                            <td>{{ Number(item.releasedToDate || 0).toLocaleString() }}</td>
                            <td>{{ Number(item.balance || 0).toLocaleString() }}</td>
                            <td>{{ item.performance }}%</td>
                            <td>
                              <input type="text" class="form-control text-center"
                                :value="formatAmount(item.amountRequired)"
                                @input="(e) => onEcoAmountInput(e, item)"
                                placeholder="0.00" />
                            </td>
                            <td>
                              <input type="text" class="form-control text-center"
                                :value="formatAmount(item.amountApproved)"
                                @input="(e) => onSubEcoApprovedInput(e, item)"
                                placeholder="0.00" />
                            </td>
                            <td>
                              <button type="button" class="btn btn-outline-danger btn-sm" @click="removeSubEcoItem(sub, i)">
                                <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                  </div>

                </div>
              </div>

              <!-- ─── Navigation Buttons ─── -->
              <div class="mt-4">
                <!-- Step 1 -->
                <div v-if="step === 1 && form.budgetYear && form.natureOfExpenditure && !ecoLoading" class="d-flex justify-content-end">
                  <button v-if="!multilayered" type="button" class="btn btn-primary submit-btn d-inline-flex align-items-center"
                    @click="handleSubmit" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <span class="me-2">{{ loading ? 'Submitting...' : 'Submit' }}</span>
                  </button>
                  <button v-else type="button" class="btn btn-primary next-btn d-inline-flex align-items-center"
                    @click="handleNext" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <span class="me-2">{{ loading ? 'Loading...' : 'Next' }}</span>
                    <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                  </button>
                </div>

                <!-- Step 2 -->
                <div v-else-if="step === 2" class="d-flex justify-content-between">
                  <div class="d-flex gap-2">
                    <button type="button" class="btn btn-secondary back-btn d-inline-flex align-items-center"
                      @click="handleBack">
                      <span class="iconify me-2" data-icon="formkit:arrowleft" style="font-size: 1.2em;"></span>
                      Previous
                    </button>
                    <button type="button" class="btn btn-outline-primary" @click="addSubApproval">
                      Add Sub-approval
                    </button>
                  </div>
                  <button type="button" class="btn btn-primary next-btn d-inline-flex align-items-center justify-content-center"
                    @click="handleSubmit" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {{ loading ? 'Submitting...' : 'Submit' }}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import { useUserStore } from '@/stores/user'
import DashboardLayout from '../../layout/DashboardLayout.vue'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const userStore = useUserStore()

const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

// ── Stepper ───────────────────────────────────────────────────────────────────
const step = ref(1)
const multilayered = ref(false)
const steps = [
  { label: 'Special Approval' },
  { label: 'Sub-special Approval' },
]
const totalSteps = computed(() => multilayered.value ? 2 : 1)
const progressWidth = computed(() => (step.value * (100 / totalSteps.value)) + '%')

// ── Years dropdown ────────────────────────────────────────────────────────────
const currentYear = new Date().getFullYear()
const years = ref(Array.from({ length: currentYear - 2025 + 1 }, (_, i) => currentYear - i))

// ── Step 1 Form ───────────────────────────────────────────────────────────────
const form = reactive({
  budgetYear: '',
  natureOfExpenditure: '',
  approvalDate: '',
  fileNumber: '',
  creatorName: '',
  sectoralHeadName: '',
  purposeOfRequest: '',
  amountRequired: '',
  amountApproved: '',
  initiatorId: '',
})

// ── Step 1 Eco Items ──────────────────────────────────────────────────────────
const ecoItems = ref([])
const searchTerm = ref('')
const newEcoId = ref(null)
const selectedEco = ref(null)

const ecoOptions = ref([])
const ecoLoading = ref(false)

const fetchEcoOptions = async () => {
  if (!form.budgetYear || !form.natureOfExpenditure) return
  const token = Cookies.get('auth_token')
  ecoLoading.value = true
  ecoOptions.value = []
  try {
    const { data } = await axios.get(
      `${root_api_url}/eli/mda-eli-budget/all/${form.budgetYear}/${form.natureOfExpenditure}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const items = Array.isArray(data) ? data : (data ? [data] : [])
    ecoOptions.value = items.map(dto => ({
      id: dto.id,
      ecoCode: dto.economicCode,
      title: dto.title,
      approvedBudget: dto.amountBudgeted,
      releasedToDate: dto.amountReleased,
      balance: dto.balance,
      performance: dto.performance,
    }))
  } catch {
    ecoOptions.value = []
  } finally {
    ecoLoading.value = false
  }
}

const savedEcoItems = ref([])

watch(multilayered, (val) => {
  if (val) {
    savedEcoItems.value = [...ecoItems.value]
    ecoItems.value = []
    newEcoId.value = null
    selectedEco.value = null
    searchTerm.value = ''
  } else {
    ecoItems.value = [...savedEcoItems.value]
    savedEcoItems.value = []
  }
})

watch([() => form.budgetYear, () => form.natureOfExpenditure], ([year, nature]) => {
  ecoItems.value = []
  ecoOptions.value = []
  newEcoId.value = null
  selectedEco.value = null
  searchTerm.value = ''
  if (year && nature) fetchEcoOptions()
})

const filteredEcoOptions = computed(() =>
  ecoOptions.value.filter(item =>
    [item.ecoCode, item.title].join(' ').toLowerCase().includes(searchTerm.value.toLowerCase())
  )
)

const onSelectEco = () => {
  selectedEco.value = ecoOptions.value.find(e => e.id === newEcoId.value) || null
}

const addEcoItem = () => {
  if (!selectedEco.value) return
  if (ecoItems.value.some(i => i.id === selectedEco.value.id)) return
  ecoItems.value.push({ ...selectedEco.value, amountRequired: '', amountApproved: '' })
  newEcoId.value = null
  selectedEco.value = null
  searchTerm.value = ''
}

const removeEcoItem = (index) => {
  ecoItems.value.splice(index, 1)
}

// ── Amount Formatting ─────────────────────────────────────────────────────────
const MAX_SAFE_RAW = Number.MAX_SAFE_INTEGER.toString()

const formatAmount = (value) => {
  if (value === '' || value === null || value === undefined) return ''
  const num = parseFloat(value).toFixed(2)
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const onAmountInput = (event, target, key) => {
  let raw = event.target.value.replace(/\D/g, '')
  if (raw === '') { target[key] = ''; event.target.value = ''; return }
  if (raw.length > MAX_SAFE_RAW.length || (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) raw = MAX_SAFE_RAW
  const num = parseInt(raw, 10) / 100
  target[key] = Number(num.toFixed(2))
  event.target.value = formatAmount(target[key])
}

const onEcoAmountInput = (event, item) => {
  let raw = event.target.value.replace(/\D/g, '')
  if (raw === '') { item.amountRequired = ''; event.target.value = ''; return }
  if (raw.length > MAX_SAFE_RAW.length || (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) raw = MAX_SAFE_RAW
  const num = parseInt(raw, 10) / 100
  item.amountRequired = Number(num.toFixed(2))
  event.target.value = formatAmount(item.amountRequired)
}

const onEcoApprovedInput = (event, item) => {
  let raw = event.target.value.replace(/\D/g, '')
  if (raw === '') { item.amountApproved = ''; event.target.value = ''; return }
  if (raw.length > MAX_SAFE_RAW.length || (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) raw = MAX_SAFE_RAW
  const num = parseInt(raw, 10) / 100
  item.amountApproved = Number(num.toFixed(2))
  event.target.value = formatAmount(item.amountApproved)
}

const onSubEcoApprovedInput = (event, item) => {
  let raw = event.target.value.replace(/\D/g, '')
  if (raw === '') { item.amountApproved = ''; event.target.value = ''; return }
  if (raw.length > MAX_SAFE_RAW.length || (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) raw = MAX_SAFE_RAW
  const num = parseInt(raw, 10) / 100
  item.amountApproved = Number(num.toFixed(2))
  event.target.value = formatAmount(item.amountApproved)
}

// ── File Upload ───────────────────────────────────────────────────────────────
const fileInput = ref(null)
const approvalDocument = ref(null)
const documentName = ref('')
const isDragging = ref(false)

const openFilePicker = () => fileInput.value?.click()

const onFileChange = (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  approvalDocument.value = file
  documentName.value = file.name
}

const onDrop = (event) => {
  isDragging.value = false
  const file = event.dataTransfer.files?.[0]
  if (!file) return
  approvalDocument.value = file
  documentName.value = file.name
}

// ── Step 2: Sub-special Approval ─────────────────────────────────────────────
const specialApprovalTypes = ref([])

const fetchSpecialApprovalTypes = async () => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/spec-approval-type/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    specialApprovalTypes.value = (Array.isArray(data) ? data : []).map(dto => ({
      id: dto.id,
      name: dto.name,
    }))
  } catch {
    specialApprovalTypes.value = []
  }
}

const subApprovals = ref([{ typeId: '', amtRequired: '', amtApproved: '', ecoItems: [], searchTerm: '', newEcoId: null }])

const subApprovalLabel = (index) => {
  return index === 0 ? 'Sub-special Approval' : `Sub-special Approval ${index + 1}`
}

const eliExceedsParent = computed(() => {
  const cap = Number(form.amountRequired || 0)
  const sum = ecoItems.value.reduce((s, item) => s + Number(item.amountRequired || 0), 0)
  return cap > 0 && sum > cap + 0.01
})


const getFilteredSubEcoOptions = (sub) =>
  ecoOptions.value.filter(item =>
    [item.ecoCode, item.title].join(' ').toLowerCase().includes((sub.searchTerm || '').toLowerCase())
  )

const addSubEcoItem = (sub) => {
  const opt = ecoOptions.value.find(e => e.id === sub.newEcoId)
  if (!opt) return
  if (sub.ecoItems.some(i => i.id === opt.id)) return
  sub.ecoItems.push({ ...opt, amountRequired: '', amountApproved: '' })
  sub.newEcoId = null
  sub.searchTerm = ''
}

const removeSubEcoItem = (sub, index) => {
  sub.ecoItems.splice(index, 1)
}

const addSubApproval = () => {
  subApprovals.value.push({ typeId: '', amtRequired: '', amtApproved: '', ecoItems: [], searchTerm: '', newEcoId: null })
}

const removeSubApproval = (index) => {
  subApprovals.value.splice(index, 1)
}

// ── Navigation ────────────────────────────────────────────────────────────────
const loading = ref(false)

const handleNext = () => {
  step.value = 2
}

const handleBack = () => {
  if (step.value > 1) step.value--
}

const handleSubmit = async () => {
  if (!multilayered.value && ecoItems.value.length === 0) {
    Swal.fire({ icon: 'warning', text: 'Please add at least one eco line item before submitting.' })
    return
  }

  if (!multilayered.value && eliExceedsParent.value) {
    Swal.fire({ icon: 'warning', text: 'ELI total amount required exceeds the amount required.' })
    return
  }

  if (!approvalDocument.value) {
    Swal.fire({ icon: 'warning', text: 'Please upload the approval document.' })
    return
  }

  if (multilayered.value) {
    for (let i = 0; i < subApprovals.value.length; i++) {
      const sub = subApprovals.value[i]
      const eliSum = (sub.ecoItems || []).reduce((s, item) => s + Number(item.amountRequired || 0), 0)
      const cap = Number(sub.amtRequired || 0)
      if (cap > 0 && eliSum > cap + 0.01) {
        Swal.fire({ icon: 'warning', text: `${subApprovalLabel(i)}: ELI total amount required exceeds the sub-approval's amount required.` })
        return
      }
    }

    const parentRequired = Number(form.amountRequired || 0)
    const parentApproved = Number(form.amountApproved || 0)
    const sumRequired = subApprovals.value.reduce((s, sub) => s + Number(sub.amtRequired || 0), 0)
    const sumApproved = subApprovals.value.reduce((s, sub) => s + Number(sub.amtApproved || 0), 0)

    if (Math.abs(sumRequired - parentRequired) > 0.01) {
      Swal.fire({
        icon: 'warning',
        text: `Sub-approval amounts required (${formatAmount(sumRequired)}) must sum to the parent amount required (${formatAmount(parentRequired)}).`,
      })
      return
    }
    if (Math.abs(sumApproved - parentApproved) > 0.01) {
      Swal.fire({
        icon: 'warning',
        text: `Sub-approval amounts approved (${formatAmount(sumApproved)}) must sum to the parent amount approved (${formatAmount(parentApproved)}).`,
      })
      return
    }
  }

  const token = Cookies.get('auth_token')

  const mapLineItems = (items) => items.map(item => ({
    mdaLineItemBudgetId: item.id || null,
    ecoCode: item.ecoCode || null,
    title: item.title || null,
    amountBudgeted: Number(item.approvedBudget || 0),
    amountReleased: Number(item.releasedToDate || 0),
    balance: Number(item.balance || 0),
    amountRequired: Number(item.amountRequired || 0),
    amountRecommended: Number(item.amountRequired || 0),
    amountApproved: Number(item.amountApproved || 0),
    quarter: 0,
    year: Number(form.budgetYear || 0),
  }))

  const dtoPayload = {
    initiatorId: form.initiatorId || null,
    initiatorName: form.creatorName || null,
    sectoralHeadName: form.sectoralHeadName || null,
    fileNumber: form.fileNumber || null,
    purposeOfRequest: form.purposeOfRequest || null,
    amtRequired: Number(form.amountRequired || 0),
    amtApproved: Number(form.amountApproved || 0),
    budgetYear: Number(form.budgetYear || 0),
    natOfExpenditure: form.natureOfExpenditure || null,
    fileApprovedDate: form.approvalDate ? new Date(form.approvalDate).toISOString() : null,
    parentApprovalInd: multilayered.value ? 1 : 0,
    lineItems: multilayered.value ? null : mapLineItems(ecoItems.value),
    subApprovals: multilayered.value
      ? subApprovals.value.map(sub => ({
          specApprovalTypeId: sub.typeId || null,
          amtRequired: Number(sub.amtRequired || 0),
          amtApproved: Number(sub.amtApproved || 0),
          lineItems: mapLineItems(sub.ecoItems || []),
        }))
      : [],
  }

  const formData = new FormData()
  formData.append('dto', new Blob([JSON.stringify(dtoPayload)], { type: 'application/json' }))
  formData.append('image', approvalDocument.value)

  loading.value = true
  try {
    await axios.post(`${root_api_url}/appraisal/special-approval`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    })

    await Swal.fire({
      html: `${iconHtmlSuccess}
        <h1 style="font-weight:600;">Success</h1>
        <p>Special Approval submitted successfully.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })

    Object.assign(form, {
      budgetYear: '', approvalDate: '', fileNumber: '',
      purposeOfRequest: '', amountRequired: '', amountApproved: '',
    })
    ecoItems.value = []
    subApprovals.value = [{ typeId: '', amtRequired: '', amtApproved: '', ecoItems: [], searchTerm: '', newEcoId: null }]
    approvalDocument.value = null
    documentName.value = ''
    multilayered.value = false
    step.value = 1
  } catch (error) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage ?? error?.response?.data?.error ?? 'Unexpected error occurred. Kindly contact administrator'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loading.value = false
  }
}

const fetchGroupHeadName = async (groupTypeId) => {
  if (!groupTypeId) return
  const token = Cookies.get('auth_token')
  try {
    const { data: groupHead } = await axios.get(`${root_api_url}/eli/group-head/${groupTypeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!groupHead?.userId) return
    const { data: user } = await axios.get(`${root_api_url}/auth/user/client/id/${groupHead.userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    form.sectoralHeadName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
  } catch {
    form.sectoralHeadName = ''
  }
}

onMounted(async () => {
  document.title = 'Create Special Approval'
  if (!userStore.profile) {
    await userStore.fetchProfile()
  }
  form.initiatorId = userStore.uuid || ''
  form.creatorName = `${userStore.profile?.firstName || ''} ${userStore.profile?.lastName || ''}`.trim()
  fetchGroupHeadName(userStore.profile?.groupTypeId)
  fetchSpecialApprovalTypes()
})
</script>

<style scoped>
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.4rem;
  margin-bottom: 1rem;
}

.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  background-color: #fafafa;
  transition: border-color 0.2s, background-color 0.2s;
  user-select: none;
}

.upload-area:hover,
.upload-area.dragging {
  border-color: #0d6efd;
  background-color: #f0f7ff;
}

.upload-icon {
  font-size: 2.5rem;
  color: #6c757d;
  display: block;
  margin-bottom: 0.5rem;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.sub-approval-card {
  background-color: #eef3ff;
  border: 1px solid #c7d7f8;
  border-radius: 0.5rem;
  padding: 1.25rem;
}
</style>
